import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import doxezLogo from '../../assets/DoxezLOGO.png';
import opheLogo from '../../assets/ophe_LOGO.png';
import authorizedSign from '../../assets/Autorized_signatory.png';
import {
    CheckCircle2,
    Clock,
    XCircle,
    Receipt,
    CreditCard,
    Building2,
    Activity,
    RefreshCw,
    FileText,
    ShieldCheck,
    CheckSquare,
    Square,
    Lock,
    Calendar,
    Sparkles,
    X
} from 'lucide-react';

const DEFAULT_OPHE_TERMS = `
<p>These Terms &amp; Conditions (“Terms”) govern the home care coordination and online consultation services facilitated through the OPHE platform, operated by <strong>Hedena Healthcare Private Limited</strong> (CIN: U86100OD2025PTC049283) (“OPHE”, “we”, “us” or “Platform”).</p>

<p>OPHE is a digital healthcare and home healthcare facilitation service operated by Hedena Healthcare Private Limited.</p>

<p>By proceeding with the booking or payment for OPHE’s home healthcare services, you, the Patient or Attendant, confirm that you have read, understood, and agreed to these Terms and Conditions.</p>

<p><strong>Patient Name:</strong> {patientName} &nbsp;|&nbsp; <strong>Booking ID:</strong> {bookingId} &nbsp;|&nbsp; <strong>Date:</strong> {dateStr}</p>

<h4>HOME HEALTHCARE &amp; CAREGIVER SERVICE CONDITIONS</h4>
<p>1. It is hereby understood that the care receiver’s general physical and mental health condition has been fully disclosed by the Client to the company and caregiver prior to commencement of service. Based on this information and other preliminary discussion, the company hereby agrees to depute a trained caregiver at the residence of the care receiver mentioned above on a mutually agreed date.</p>
<p>2. The caregiver will do the necessary daily patient care activities, as deemed fit to the best of her/his ability based on the basis of the briefing of the client and/or doctor’s prescription, if any.</p>
<p>3. The caregiver would engage herself/himself in patient care and related hygiene and sanitary activities. The <strong>caregiver should not be engaged in any household work</strong>. In such an event, the Company may decide to withdraw its service with 7 days’ notice.</p>
<p>4. Since the health care assistant will be residing at the residence of the client, the caregiver should be given adequate rest of 8 hours per day and should be provided 3 meals per day and tea and snacks. The client is required to arrange for adequate space and a sleeping mattress for the caregiver to sleep at night.</p>
<p>5. The caregiver shall be treated with respect and the personal safety and security of the caregiver will be the Client’s responsibility.</p>
<p>6. The client shall ensure that the valuables e.g ornaments, small electronic gadgets, watches, cash etc. are kept in safe custody and the company or its caregiver should not be held liable for any loss of such articles.</p>
<p>7. The caregiver is entitled to 4 days of leave per month which may be availed by the caregiver every month or the accumulated leave can be availed once in 3 months, which will be decided as per the mutual convenience of the Client and the caregiver. For the patients in Bhubaneswar/Cuttack, during this period, the Company will provide a replacement caregiver either on monthly or three-monthly basis, as the case may be. For outstation clients, the replacement will be given after a minimum period of three months only.</p>
<p>8. For engagements outside the state of Odisha, three monthly to and fro travel arrangement of the caregiver will be done by the client at his/her own cost.</p>
<p>9. In case the Client finds any deficiency in the service of the caregiver deputed by the Company, then he/she will notify the same to the Company and may ask for a change of caregiver and the Company will make efforts to give a replacement within next 15 days.</p>
<p>10. The Company would arrange to provide substitute (backup) in the absence of the caregiver not being able to continue in the service for any reason.</p>
<p>11. In the unlikely event of the Company being unable to provide the back-up service, it will return the amount due to the Client after the deduction proportionate to the service already rendered, if any.</p>
<p>12. The Client shall not hire the caregiver directly by bypassing the Company. In the unlikely event of such an occurrence, the Client will be liable to pay an equivalent of three months of the monthly fee to the Company.</p>
<p>13. In the unlikely event of death of the patient (Client’s relative) during the course of the caregiving for any reason whatsoever, neither the Company nor the caregiver will be held liable for such an eventuality.</p>
<p>14. After the registration, the Company will interact with the client either in person or through video conference. This is to understand the requirement of either side for smooth commencement of service.</p>

<h4>1. NATURE OF OPHE SERVICE</h4>
<p>OPHE is a digital healthcare and home care facilitation platform operated by Hedena Healthcare Private Limited that facilitates online consultations and care coordination between patients, attendants, and appropriately empanelled/associated Registered Medical Practitioners (“RMPs”) and professional caregivers.</p>
<p>OPHE does not itself diagnose, prescribe, treat, or make clinical decisions. All medical opinions, diagnoses, prescriptions, treatment recommendations and referrals are made independently by the concerned RMP based on professional medical judgment.</p>

<h4>2. ONLINE CONSULTATION</h4>
<p>The consultation may be conducted through permitted electronic modes, including video, audio or other available telemedicine communication methods, as clinically appropriate.</p>
<p>The RMP may review the information, medical history, reports, photographs, prescriptions or other documents provided by the Patient before or during the consultation.</p>
<p>The RMP may, based on clinical judgment:</p>
<ul>
  <li>Provide a medical consultation;</li>
  <li>Provide an electronic prescription where clinically and legally appropriate;</li>
  <li>Recommend investigations;</li>
  <li>Recommend physical consultation;</li>
  <li>Refer the Patient to another specialist or healthcare facility;</li>
  <li>Recommend hospital admission or further medical evaluation; or</li>
  <li>Decline to provide a prescription where sufficient clinical information is not available or teleconsultation is not considered appropriate.</li>
</ul>
<p>Payment for a consultation does not guarantee a prescription, diagnosis, particular medicine, treatment, referral or clinical outcome.</p>

<h4>3. PATIENT INFORMATION</h4>
<p>The Patient/Attendant is responsible for providing accurate and complete information relevant to the consultation, including, where applicable:</p>
<ul>
  <li>Name and age/date of birth;</li>
  <li>Relevant medical history;</li>
  <li>Current symptoms;</li>
  <li>Existing medical conditions;</li>
  <li>Current medications;</li>
  <li>Known allergies;</li>
  <li>Previous prescriptions;</li>
  <li>Investigation reports;</li>
  <li>Relevant photographs or other clinical information.</li>
</ul>
<p>The RMP may request additional information, reports, photographs or documents where considered necessary for the consultation.</p>
<p>The RMP may rely upon the information provided by the Patient/Attendant for the purpose of the consultation.</p>
<p>Any material omission, inaccurate information or incomplete disclosure may affect the quality or appropriateness of the medical assessment.</p>

<h4>4. CLINICAL INDEPENDENCE</h4>
<p>The RMP shall independently exercise professional medical judgment.</p>
<p>OPHE does not control or influence the RMP's diagnosis, prescription, treatment recommendation, referral decision or other clinical decision.</p>
<p>The RMP may determine that an online consultation is insufficient and may advise the Patient to undergo physical examination, diagnostic evaluation, hospital consultation or other appropriate medical care.</p>

<h4>5. ELECTRONIC PRESCRIPTION</h4>
<p>Where clinically appropriate and legally permitted, the RMP may issue an electronic prescription following the consultation.</p>
<p>The prescription shall be issued by the concerned RMP and shall contain the applicable professional identification and registration details as required under applicable regulations.</p>
<p>The Patient acknowledges that medicines may be prescribed, modified, restricted or withheld entirely at the independent discretion of the RMP and in accordance with applicable medical regulations.</p>

<h4>6. LIMITATIONS OF TELEMEDICINE</h4>
<p>The Patient acknowledges that an online consultation has inherent limitations because the RMP may not be able to physically examine the Patient or perform certain clinical assessments.</p>
<p>Accordingly, an online consultation does not necessarily replace an in-person medical consultation.</p>
<p>Where the RMP considers physical examination or further evaluation necessary, the Patient should follow such recommendation without undue delay.</p>

<h4>7. EMERGENCY MEDICAL CONDITIONS</h4>
<p>OPHE is not an emergency medical response or ambulance service.</p>
<p>The online consultation service should not be used as a substitute for emergency medical care.</p>
<p>In case of severe or potentially life-threatening symptoms, including severe chest pain, severe breathing difficulty, loss of consciousness, major bleeding, severe trauma, suspected stroke, seizure, severe allergic reaction, poisoning or any other medical emergency, the Patient should immediately contact appropriate emergency medical services or proceed to the nearest emergency facility.</p>

<h4>8. CONSULTATION FEE &amp; PAYMENT</h4>
<p>The consultation fee displayed against the relevant Booking ID/Patient ID is the applicable fee for the selected online consultation or home care service. Payment shall ordinarily be made through the payment facility provided by OPHE or its authorised payment service provider. The consultation/service shall ordinarily be confirmed after successful payment.</p>

<h4>9. CANCELLATION, RESCHEDULING &amp; REFUND</h4>
<p>Where the consultation has not commenced and the Patient requests cancellation, refund or rescheduling, the request may be considered in accordance with the applicable operational policy. Once clinical information has been reviewed, medical advice rendered, or home care scheduling initiated, fees may be non-refundable.</p>

<h4>10. CONSULTATION LINK &amp; ACCESS</h4>
<p>The consultation link or access details may be shared through registered communication channels, including WhatsApp, SMS or email. The Patient should ensure suitable internet connectivity, compatible device, and a reasonably private environment.</p>

<h4>11. PATIENT / ATTENDANT / FAMILY MEMBER</h4>
<p>A Patient may participate in the consultation with an attendant or family member where appropriate. For minors or persons unable to provide consent independently, the applicable guardian requirements shall be followed.</p>

<h4>12. PRIVACY &amp; CONFIDENTIALITY</h4>
<p>OPHE may collect and process information reasonably required for booking, consultation coordination, payment processing, prescription delivery, and legal compliance in accordance with the OPHE Privacy Policy.</p>

<h4>13. MEDICAL RECORDS &amp; PRESCRIPTION RECORDS</h4>
<p>Relevant consultation information, documents, prescriptions and records may be retained by OPHE and/or the concerned RMP to the extent required or permitted under applicable law.</p>

<h4>14. RECORDING &amp; SHARING OF CONSULTATION</h4>
<p>The Patient/Attendant shall not record, reproduce, publish, distribute or commercially use consultation content without appropriate consent.</p>

<h4>15. CONSULTATION OUTCOME</h4>
<p>Medical outcomes cannot be guaranteed through telemedicine or any medical consultation. The assessment is based on the information reasonably available during the consultation.</p>

<h4>16. FOLLOW-UP CONSULTATION</h4>
<p>Where follow-up is considered clinically appropriate, the RMP may recommend another online consultation, physical consultation, or hospital visit.</p>

<h4>17. HOSPITAL / SPECIALIST REFERRAL</h4>
<p>Where clinically appropriate, the RMP may recommend consultation with another specialist or hospital admission. OPHE may assist with network coordination.</p>

<h4>18. LIMITATION OF OPHE'S ROLE</h4>
<p>OPHE facilitates technology, communication, scheduling, payment coordination and healthcare journey support. Clinical responsibility remains with the treating RMP within the scope of the consultation.</p>

<h4>19. TECHNICAL / OPERATIONAL INTERRUPTIONS</h4>
<p>OPHE makes reasonable efforts for uninterrupted services, but temporary technical or connectivity disruptions may occur.</p>

<h4>20. PATIENT RESPONSIBILITY</h4>
<p>The Patient is responsible for providing accurate information, following medical advice, and completing recommended tests.</p>

<h4>21. CUSTOMER SUPPORT &amp; GRIEVANCE</h4>
<p>For booking, payment, or platform support, contact Customer Support at support@doxez.in or +91-9692949500.</p>

<h4>22. ELECTRONIC ACCEPTANCE</h4>
<p>Acceptance of these Terms through an electronic checkbox, button, or digital mechanism is recorded against the relevant Booking ID/Patient ID.</p>

<h4>23. MODIFICATION OF TERMS</h4>
<p>Terms may be updated from time to time. Terms applicable are those presented at the time of booking and payment.</p>

<h4>24. GOVERNING LAW</h4>
<p>These Terms shall be governed by the laws of India, under the jurisdiction of competent courts in Odisha, India.</p>

<h4>25. ACCEPTANCE OF TERMS</h4>
<p>By proceeding with payment and using the service, the Patient/Attendant confirms that information provided is accurate, understands the limitations of online consultation, and agrees to these Terms &amp; Conditions.</p>

<div style="margin-top: 24px; text-align: right; display: flex; flex-direction: column; align-items: flex-end;">
  <p style="margin: 0; font-size: 13px; font-weight: 700; color: #0F172A;">Sampath Kumar</p>
  <p style="margin: 2px 0 0 0; font-size: 12px; font-weight: 600; color: #64748B;">Operations Manager</p>
  <p style="margin: 2px 0 0 0; font-size: 12px; font-weight: 700; color: #0D524A;">Hedena Healthcare Pvt. Ltd.</p>
</div>
`;

const PaymentStatus = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [lead, setLead] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Terms & Conditions Acceptance State for OPHE
    const [termsTitle, setTermsTitle] = useState('OPHE HOME HEALTHCARE SERVICES – TERMS & CONDITIONS');
    const [termsContent, setTermsContent] = useState(DEFAULT_OPHE_TERMS);
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [processingPayment, setProcessingPayment] = useState(false);
    const hasSyncedRef = useRef(false);

    const fetchStatus = async (showLoading = false) => {
        try {
            if (showLoading) setLoading(true);

            const rawApi = import.meta.env.VITE_API_URL || '';
            const apiBase = rawApi.endsWith('/api') ? rawApi : (rawApi ? `${rawApi}/api` : '/api');

            // Auto-sync with Razorpay if returned from checkout callback (guarded so it only fires once per page load)
            const queryPaymentId = searchParams.get('razorpay_payment_id') || searchParams.get('payment_id');
            const queryLinkId = searchParams.get('razorpay_payment_link_id');
            if ((queryPaymentId || queryLinkId) && !hasSyncedRef.current) {
                hasSyncedRef.current = true;
                try {
                    await axios.post(`${apiBase}/payments/public-sync`, {
                        leadId: id,
                        razorpayPaymentId: queryPaymentId,
                        razorpayPaymentLinkId: queryLinkId
                    });
                } catch (syncErr) {
                    console.warn('[PaymentStatus] Sync failed:', syncErr.message);
                }
            }

            const res = await axios.get(`${apiBase}/leads/public/${id}`);
            if (res.data?.data) {
                setLead(res.data.data);
            } else {
                throw new Error('Lead data missing');
            }
        } catch (err) {
            console.error('[PaymentStatus] Error:', err);
            if (err.response?.status === 404 || err.response?.status === 400) {
                setLead(null);
            }
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchStatus(false);
        setRefreshing(false);
    };

    const handleClose = () => {
        try {
            window.close();
        } catch (_) {}
        setTimeout(() => {
            if (!window.closed) {
                if (window.history.length > 1) {
                    window.history.back();
                } else {
                    navigate('/');
                }
            }
        }, 150);
    };

    useEffect(() => {
        if (id) fetchStatus(true);
    }, [id]);

    const isOpheLead = Boolean(
        lead?.pipelineChoice === 'ophe' ||
        lead?.leadType === 'ophe' ||
        Boolean(lead?.ophePaymentData) ||
        searchParams.get('type') === 'ophe'
    );

    const queryPaymentId = searchParams.get('razorpay_payment_id') || searchParams.get('payment_id');

    // ── Payment Confirmation Evaluation ──
    // For OPHE leads:
    // A payment is confirmed ONLY when ophePaymentData.status === 'paid'
    // or when returning with a verified queryPaymentId from Razorpay.
    // NOTE: An OPHE lead must NEVER be marked confirmed based on registrationStep >= 2
    // or status === 'care_in_progress', because CC registration sets registrationStep to 5
    // and status is often care_in_progress before or during care service!
    const isOpheConfirmed = isOpheLead && Boolean(
        lead?.ophePaymentData?.status === 'paid' ||
        (queryPaymentId && (lead?.paymentStatus === 'paid' || lead?.ophePaymentData?.status === 'paid'))
    );

    // For DOXEZ surgical leads:
    const isDoxezConfirmed = !isOpheLead && Boolean(
        lead && (
            ['paid', 'surgery', 'closure', 'payout_processing', 'completed'].includes(lead.status) ||
            lead.paymentStatus === 'paid' ||
            Boolean(queryPaymentId)
        )
    );

    const isConfirmed = isOpheLead ? isOpheConfirmed : isDoxezConfirmed;

    // Fetch dynamic MoU terms from backend
    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const rawApi = import.meta.env.VITE_API_URL || '';
                const apiBase = rawApi.endsWith('/api') ? rawApi : (rawApi ? `${rawApi}/api` : '/api');
                const res = await axios.get(`${apiBase}/mou/templates/ophe`);
                if (res.data?.data) {
                    if (res.data.data.title) setTermsTitle(res.data.data.title);
                    if (res.data.data.content) setTermsContent(res.data.data.content);
                }
            } catch (err) {
                console.warn('[PaymentStatus] Using default OPHE terms fallback:', err.message);
            }
        };

        if (isOpheLead) {
            fetchTerms();
        }
    }, [isOpheLead]);

    // Poll status ONLY when returning with payment query or for surgical leads
    useEffect(() => {
        let interval;
        const hasPaymentQuery = Boolean(searchParams.get('razorpay_payment_id') || searchParams.get('payment_id'));
        if (lead && !isConfirmed && (!isOpheLead || hasPaymentQuery)) {
            interval = setInterval(() => fetchStatus(false), 5000);
        }
        return () => clearInterval(interval);
    }, [id, lead?.status, isConfirmed, isOpheLead, searchParams]);


    // Format terms HTML with placeholders replaced
    const getFormattedTermsHtml = () => {
        const dateStr = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
        const bookingId = lead?.leadId || lead?.bookingId || (lead?._id ? `OPHE-${String(lead._id).slice(-6).toUpperCase()}` : 'N/A');
        const pName = lead?.patientName || 'Patient';
        const pPhone = lead?.patientPhone || lead?.alternatePhone || 'N/A';
        const pEmail = lead?.patientEmail || lead?.alternateEmail || 'N/A';
        const addr = lead?.patientAddress || lead?.address || lead?.city || 'Odisha, India';

        let html = termsContent || DEFAULT_OPHE_TERMS;
        html = html
            .replace(/<hr[^>]*\/?>[\s\S]*?(?:Hedena Healthcare|U86100OD2025PTC049283|Healthcare Facilitation|Sampath Kumar)[\s\S]*$/gi, '')
            .replace(/<p[^>]*>[\s\S]*?(?:Hedena Healthcare|U86100OD2025PTC049283|Healthcare Facilitation|Sampath Kumar)[\s\S]*?<\/p>\s*$/gi, '')
            .replace(/<div[^>]*>[\s\S]*?Sampath Kumar[\s\S]*?<\/div>\s*$/gi, '')
            .replace(/<hr[^>]*\/?>\s*$/gi, '');

        html += `
<div style="margin-top: 24px; text-align: right; display: flex; flex-direction: column; align-items: flex-end;">
  <img src="${authorizedSign}" alt="Authorized Signatory" style="width: 100px; height: auto; margin-bottom: 4px;" />
  <p style="margin: 0; font-size: 13px; font-weight: 700; color: #0F172A;">Sampath Kumar</p>
  <p style="margin: 2px 0 0 0; font-size: 12px; font-weight: 600; color: #64748B;">Operations Manager</p>
  <p style="margin: 2px 0 0 0; font-size: 12px; font-weight: 700; color: #0D524A;">Hedena Healthcare Pvt. Ltd.</p>
</div>`;

        return html
            .replace(/{dateStr}/g, dateStr)
            .replace(/{patientName}/g, pName)
            .replace(/{bookingId}/g, bookingId)
            .replace(/{patientPhone}/g, pPhone)
            .replace(/{patientEmail}/g, pEmail)
            .replace(/{address}/g, addr);
    };

    const handleProceedToPayment = async () => {
        if (!termsAgreed) {
            alert('Please check the box to agree to the Terms & Conditions before proceeding.');
            return;
        }

        const paymentLink = lead?.ophePaymentData?.paymentLink || lead?.razorpayDetails?.paymentLink || lead?.billingDetails?.paymentLink;
        if (!paymentLink) {
            alert('Payment link not available. Please contact our care coordinator.');
            return;
        }

        try {
            setProcessingPayment(true);
            const rawApi = import.meta.env.VITE_API_URL || '';
            const apiBase = rawApi.endsWith('/api') ? rawApi : (rawApi ? `${rawApi}/api` : '/api');

            // Record terms acceptance on backend
            await axios.patch(`${apiBase}/leads/public/${id}`, {
                termsAccepted: true
            }).catch(() => { });

            // Redirect to Razorpay payment link
            window.location.href = paymentLink;
        } catch (err) {
            console.error('[PaymentStatus] Proceed to payment error:', err);
            window.location.href = paymentLink;
        } finally {
            setProcessingPayment(false);
        }
    };

    // ─── Loading State ────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.loadingWrap}>
                    <div style={styles.spinner} />
                    <p style={styles.loadingText}>Loading Care Details…</p>
                </div>
            </div>
        );
    }

    // ─── Error State ──────────────────────────────────────────────────────────
    if (!lead) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <img src={doxezLogo} alt="Doxez" style={styles.logo} />
                    </div>
                    <div style={{ padding: '40px 32px', textAlign: 'center' }}>
                        <div style={{ ...styles.iconCircle, background: '#FEE2E2' }}>
                            <XCircle size={32} color="#EF4444" />
                        </div>
                        <h2 style={{ ...styles.title, marginTop: 20 }}>Link Error</h2>
                        <p style={styles.subtitle}>This care link is invalid, expired, or the record has been archived.</p>
                        <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <button style={styles.btnPrimary} onClick={() => fetchStatus(true)}>Retry</button>
                            <button style={styles.btnSecondary} onClick={() => navigate('/')}>Back to Home</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const { patientName, assignedHospital } = lead;
    const paymentIdToShow = queryPaymentId || lead?.ophePaymentData?.razorpayPaymentId || lead?.razorpayDetails?.paymentId;
    const amountRaw = lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.quotedPrice || lead?.billingDetails?.totalPayableAmount || lead?.billingDetails?.procedureAmount || searchParams.get('amount');
    const amountToShow = amountRaw && Number(amountRaw) > 0 ? Number(amountRaw) : null;
    const bookingId = lead?.leadId || lead?.bookingId || (lead?._id ? `OPHE-${String(lead._id).slice(-6).toUpperCase()}` : 'N/A');
    const serviceName = lead?.opheServiceDetails?.packageName || lead?.treatmentRequired || 'OPHE Home Elder Care Service';
    const durationText = lead?.opheServiceDetails?.daysCount ? `${lead.opheServiceDetails.daysCount} Days` : (lead?.opheServiceDetails?.duration || '1 Month');
    const shiftSchedule = lead?.opheServiceDetails?.shiftType || (lead?.opheServiceDetails?.shiftHours ? `${lead.opheServiceDetails.shiftHours}-Hour Shift` : '12-Hour Shift');

    // ══════════════════════════════════════════════════════════════════════════
    // VIEW 1: UNPAID OPHE LEAD → TERMS & CONDITIONS + PAYMENT CONSENT PORTAL
    // ══════════════════════════════════════════════════════════════════════════
    if (!isConfirmed && isOpheLead) {
        return (
            <div style={styles.page}>
                <div style={styles.blob1} />
                <div style={styles.blob2} />

                <div style={{ ...styles.card, maxWidth: 640 }}>
                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #042f2e 0%, #0f766e 100%)',
                        padding: '24px 28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <img src={opheLogo} alt="OPHE" style={{ height: 44, maxWidth: 180, objectFit: 'contain' }} />
                        </div>
                        <div style={{
                            background: 'rgba(255,255,255,0.12)',
                            borderRadius: 12,
                            padding: '6px 12px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            fontSize: 11,
                            fontWeight: 800,
                            color: '#99f6e4',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                        }}>
                            <Lock size={12} /> Secure Portal
                        </div>
                    </div>

                    {/* Accent strip */}
                    <div style={{ height: 4, background: 'linear-gradient(90deg, #0d9488, #14b8a6, #2dd4bf)' }} />

                    <div style={{ padding: '28px 28px 24px' }}>
                        {/* Title & Context */}
                        <div style={{ textAlign: 'left', marginBottom: 20 }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#F0FDFA', padding: '4px 10px', borderRadius: 8, border: '1px solid #CCFBF1', marginBottom: 8 }}>
                                <Sparkles size={14} color="#0F766E" />
                                <span style={{ fontSize: 11, fontWeight: 800, color: '#0F766E', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Booking ID: {bookingId}
                                </span>
                            </div>
                            <h1 style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0', letterSpacing: '-0.02em' }}>
                                Terms of Service &amp; Complete Payment
                            </h1>
                            <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                                Please review your care booking summary and the official terms of service below. Check the agreement box to proceed to secure payment.
                            </p>
                        </div>

                        {/* Booking Summary Box */}
                        <div style={{
                            background: '#F8FAFC',
                            borderRadius: 18,
                            padding: '16px 20px',
                            border: '1.5px solid #E2E8F0',
                            marginBottom: 20,
                            textAlign: 'left'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #E2E8F0', paddingBottom: 10 }}>
                                <div>
                                    <span style={{ fontSize: 10, fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block' }}>
                                        Care Package
                                    </span>
                                    <span style={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>
                                        {serviceName}
                                    </span>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{ fontSize: 10, fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block' }}>
                                        Total Payable
                                    </span>
                                    <span style={{ fontSize: 20, fontWeight: 900, color: '#0F766E' }}>
                                        ₹{amountToShow ? amountToShow.toLocaleString('en-IN') : '—'}
                                    </span>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, fontSize: 12 }}>
                                <div>
                                    <span style={{ color: '#64748B', fontWeight: 600 }}>Patient: </span>
                                    <strong style={{ color: '#1E293B' }}>{patientName}</strong>
                                </div>
                                <div>
                                    <span style={{ color: '#64748B', fontWeight: 600 }}>Duration: </span>
                                    <strong style={{ color: '#1E293B' }}>{durationText}</strong>
                                </div>
                                <div>
                                    <span style={{ color: '#64748B', fontWeight: 600 }}>Shift: </span>
                                    <strong style={{ color: '#1E293B' }}>{shiftSchedule}</strong>
                                </div>
                            </div>
                        </div>

                        {/* Scrollable Terms & Conditions Box */}
                        <div style={{ textAlign: 'left', marginBottom: 20 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FileText size={16} color="#0F172A" />
                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {termsTitle}
                                    </span>
                                </div>
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8' }}>
                                    Scroll to review all 25 clauses
                                </span>
                            </div>

                            <div
                                className="ophe-terms-scroll-box"
                                style={{
                                    height: 320,
                                    overflowY: 'auto',
                                    background: '#FFFFFF',
                                    borderRadius: 16,
                                    border: '1.5px solid #CBD5E1',
                                    padding: '18px 20px',
                                    fontSize: 12.5,
                                    lineHeight: 1.6,
                                    color: '#334155',
                                    boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.03)'
                                }}
                                dangerouslySetInnerHTML={{ __html: getFormattedTermsHtml() }}
                            />
                        </div>

                        {/* Interactive Agreement Checkbox */}
                        <div
                            onClick={() => setTermsAgreed(!termsAgreed)}
                            style={{
                                background: termsAgreed ? '#F0FDF4' : '#F8FAFC',
                                border: termsAgreed ? '2px solid #22C55E' : '2px solid #E2E8F0',
                                borderRadius: 16,
                                padding: '14px 18px',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 12,
                                cursor: 'pointer',
                                textAlign: 'left',
                                marginBottom: 20,
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <div style={{ marginTop: 2, flexShrink: 0 }}>
                                {termsAgreed ? (
                                    <CheckSquare size={20} color="#16A34A" />
                                ) : (
                                    <Square size={20} color="#94A3B8" />
                                )}
                            </div>
                            <div>
                                <p style={{ margin: '0 0 2px 0', fontSize: 13, fontWeight: 800, color: termsAgreed ? '#15803D' : '#1E293B', lineHeight: 1.4 }}>
                                    I have read, understood, and accept the OPHE Terms &amp; Conditions
                                </p>
                                <p style={{ margin: 0, fontSize: 11, color: '#64748B', lineHeight: 1.4 }}>
                                    I understand that medical opinions and care assessments are rendered under independent clinical discretion and agree to proceed with payment.
                                </p>
                            </div>
                        </div>

                        {/* Proceed to Payment CTA Button */}
                        <button
                            type="button"
                            onClick={handleProceedToPayment}
                            disabled={!termsAgreed || processingPayment}
                            style={{
                                width: '100%',
                                padding: '18px 24px',
                                background: termsAgreed
                                    ? 'linear-gradient(135deg, #0F766E 0%, #042F2E 100%)'
                                    : '#E2E8F0',
                                color: termsAgreed ? '#FFFFFF' : '#94A3B8',
                                border: 'none',
                                borderRadius: 16,
                                fontSize: 15,
                                fontWeight: 900,
                                fontFamily: "'Inter', sans-serif",
                                letterSpacing: '0.04em',
                                cursor: termsAgreed && !processingPayment ? 'pointer' : 'not-allowed',
                                textTransform: 'uppercase',
                                boxShadow: termsAgreed ? '0 10px 24px rgba(15,118,110,0.35)' : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10
                            }}
                        >
                            <Lock size={18} />
                            {processingPayment ? 'Opening Secure Gateway…' : `Agree & Complete Payment — ₹${amountToShow ? amountToShow.toLocaleString('en-IN') : '0'}`}
                        </button>

                        <p style={{ margin: '14px 0 0 0', fontSize: 11, color: '#94A3B8', textAlign: 'center', fontWeight: 600 }}>
                            🔒 256-Bit SSL Encrypted • Powered by Razorpay &amp; Hedena Healthcare Private Limited
                        </p>
                    </div>

                    <div style={styles.footer}>
                        <p style={styles.footerText}>OPHE • Hedena Healthcare Private Limited (CIN: U86100OD2025PTC049283)</p>
                    </div>
                </div>

                {/* Scoped CSS for Contract Typography */}
                <style>{`
                    .ophe-terms-scroll-box h4 {
                        font-size: 13px;
                        font-weight: 800;
                        color: #0F172A;
                        margin-top: 14px;
                        margin-bottom: 4px;
                        text-transform: uppercase;
                        letter-spacing: 0.02em;
                    }
                    .ophe-terms-scroll-box p {
                        margin-top: 4px;
                        margin-bottom: 8px;
                        line-height: 1.55;
                    }
                    .ophe-terms-scroll-box ul {
                        margin: 4px 0 10px 18px;
                        padding-left: 0;
                    }
                    .ophe-terms-scroll-box li {
                        margin-bottom: 3px;
                    }
                `}</style>
            </div>
        );
    }

    // ══════════════════════════════════════════════════════════════════════════
    // VIEW 2: CONFIRMED PAYMENT OR SURGICAL VERIFICATION
    // ══════════════════════════════════════════════════════════════════════════
    return (
        <div style={styles.page}>
            <div style={styles.blob1} />
            <div style={styles.blob2} />

            <div style={styles.card}>
                {/* Dark Header with Logo */}
                <div style={styles.cardHeader}>
                    <img src={isOpheLead ? opheLogo : doxezLogo} alt={isOpheLead ? 'OPHE Logo' : 'Doxez Logo'} style={styles.logo} />
                </div>

                {/* Accent bar */}
                <div style={{ height: 4, background: isConfirmed ? 'linear-gradient(90deg,#10b981,#34d399)' : 'linear-gradient(90deg,#3b82f6,#60a5fa)' }} />

                <div style={styles.cardBody}>
                    {/* Status Icon */}
                    <div style={{ position: 'relative', marginBottom: 24 }}>
                        <div style={{
                            ...styles.iconCircle,
                            background: isConfirmed ? 'rgba(16,185,129,0.12)' : 'rgba(59,130,246,0.12)',
                            width: 96,
                            height: 96,
                        }}>
                            {/* Ping ring */}
                            <div style={{
                                position: 'absolute', inset: 0, borderRadius: '50%',
                                background: isConfirmed ? 'rgba(16,185,129,0.2)' : 'rgba(59,130,246,0.2)',
                                animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite',
                            }} />
                            <div style={{
                                width: 60, height: 60, borderRadius: '50%',
                                background: isConfirmed ? '#10b981' : '#3b82f6',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: isConfirmed ? '0 8px 24px rgba(16,185,129,0.35)' : '0 8px 24px rgba(59,130,246,0.35)',
                            }}>
                                {isConfirmed
                                    ? <CheckCircle2 size={28} color="#fff" />
                                    : <Clock size={28} color="#fff" />
                                }
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <h1 style={styles.title}>
                        {isConfirmed ? 'Payment Successful!' : 'Payment Processing'}
                    </h1>
                    <p style={styles.subtitle}>
                        {isConfirmed
                            ? 'Your transaction is confirmed. The care coordination team has been notified.'
                            : 'We are verifying your payment with the bank. This usually takes a few seconds.'}
                    </p>

                    {/* Post-Payment Invoice & Terms PDF Notification */}
                    {isConfirmed && (
                        <div style={{
                            marginTop: 18,
                            padding: '14px 16px',
                            background: '#ECFDF5',
                            borderRadius: 16,
                            border: '1.5px solid #A7F3D0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            textAlign: 'left'
                        }}>
                            <ShieldCheck size={26} color="#059669" style={{ flexShrink: 0 }} />
                            <p style={{ margin: 0, fontSize: 12, color: '#065F46', lineHeight: 1.5, fontWeight: 700 }}>
                                Your official <strong>Payment Confirmation Invoice</strong> and signed <strong>{isOpheLead ? 'OPHE' : 'DOXEZ'} Terms &amp; Conditions PDF</strong> have been emailed to {lead?.patientEmail || lead?.alternateEmail || 'your email'}.
                            </p>
                        </div>
                    )}

                    {/* Info Card */}
                    <div style={styles.infoCard}>
                        {/* Patient */}
                        <InfoRow
                            icon={<Activity size={20} color="#10b981" />}
                            label="Patient Name"
                            value={patientName || '—'}
                        />
                        <Divider />

                        {/* Hospital / Care Service */}
                        <InfoRow
                            icon={<Building2 size={20} color="#3b82f6" />}
                            label={isOpheLead ? 'Care Service' : 'Hospital'}
                            value={isOpheLead ? (lead?.opheServiceDetails?.packageName || 'OPHE Home Care Service') : (assignedHospital?.hospitalName || 'Clinical Partner')}
                        />

                        {/* Amount */}
                        {amountToShow && (
                            <>
                                <Divider />
                                <InfoRow
                                    icon={<CreditCard size={20} color="#8b5cf6" />}
                                    label="Amount Paid"
                                    value={`₹${amountToShow.toLocaleString('en-IN')}`}
                                    valueStyle={{ color: '#10b981', fontSize: 22, fontWeight: 800 }}
                                />
                            </>
                        )}

                        {/* Payment ID */}
                        {paymentIdToShow && (
                            <>
                                <Divider />
                                <InfoRow
                                    icon={<Receipt size={20} color="#f59e0b" />}
                                    label="Payment ID"
                                    value={paymentIdToShow}
                                    valueStyle={{ fontFamily: 'monospace', fontSize: 13, color: '#475569', userSelect: 'all' }}
                                />
                            </>
                        )}
                    </div>


                    {/* Buttons */}
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
                        <button
                            className="ps-btn-primary"
                            style={{ ...styles.btnPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: refreshing ? 0.7 : 1 }}
                            onClick={handleRefresh}
                            disabled={refreshing}
                        >
                            <RefreshCw size={16} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                            {refreshing ? 'Refreshing…' : 'Refresh Status'}
                        </button>

                        <button
                            type="button"
                            className="ps-btn-secondary"
                            style={{
                                ...styles.btnSecondary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                            }}
                            onClick={handleClose}
                        >
                            <X size={16} />
                            <span>Close</span>
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <p style={styles.footerText}>🔒 Secured by Doxez Fintech · Powered by Razorpay</p>
                </div>
            </div>

            {/* CSS Animations */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                @keyframes ping {
                    75%, 100% { transform: scale(1.6); opacity: 0; }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(24px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes spinLoader {
                    to { transform: rotate(360deg); }
                }
                .ps-btn-primary:active {
                    transform: scale(0.98);
                }
                .ps-btn-secondary:hover {
                    background: #f1f5f9 !important;
                    border-color: #cbd5e1 !important;
                    color: #0f172a !important;
                }
                .ps-btn-secondary:active {
                    transform: scale(0.98);
                }
            `}</style>
        </div>
    );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const InfoRow = ({ icon, label, value, valueStyle = {} }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
            width: 44, height: 44, borderRadius: 14,
            background: '#fff', border: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
            {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0, marginBottom: 2 }}>
                {label}
            </p>
            <p style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0, wordBreak: 'break-all', ...valueStyle }}>
                {value}
            </p>
        </div>
    </div>
);

const Divider = () => (
    <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        position: 'relative',
        overflow: 'hidden',
    },
    blob1: {
        position: 'fixed', top: -100, right: -100,
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    blob2: {
        position: 'fixed', bottom: -100, left: -100,
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    card: {
        width: '100%',
        maxWidth: 480,
        background: '#ffffff',
        borderRadius: 32,
        overflow: 'hidden',
        boxShadow: '0 40px 80px -20px rgba(0,0,0,0.5)',
        animation: 'fadeIn 0.4s ease-out',
        position: 'relative',
        zIndex: 1,
    },
    cardHeader: {
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    logo: {
        height: 48,
        maxWidth: 200,
        objectFit: 'contain',
        filter: 'brightness(1)',
    },
    cardBody: {
        padding: '36px 32px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
    iconCircle: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    title: {
        fontSize: 26,
        fontWeight: 900,
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.03em',
    },
    subtitle: {
        fontSize: 14,
        fontWeight: 500,
        color: '#64748b',
        margin: '10px 0 0',
        lineHeight: 1.6,
        maxWidth: 340,
    },
    infoCard: {
        width: '100%',
        marginTop: 24,
        background: '#f8fafc',
        borderRadius: 20,
        padding: '8px 20px',
        border: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        textAlign: 'left',
    },
    btnPrimary: {
        width: '100%',
        padding: '16px 24px',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: 16,
        fontSize: 14,
        fontWeight: 700,
        fontFamily: "'Inter', sans-serif",
        letterSpacing: '0.04em',
        cursor: 'pointer',
        textTransform: 'uppercase',
        boxShadow: '0 8px 20px rgba(15,23,42,0.3)',
        transition: 'transform 0.15s, box-shadow 0.15s',
    },
    btnSecondary: {
        width: '100%',
        padding: '16px 24px',
        background: '#fff',
        color: '#475569',
        border: '1.5px solid #e2e8f0',
        borderRadius: 16,
        fontSize: 14,
        fontWeight: 700,
        fontFamily: "'Inter', sans-serif",
        letterSpacing: '0.04em',
        cursor: 'pointer',
        textTransform: 'uppercase',
        transition: 'background 0.15s',
    },
    footer: {
        background: '#f8fafc',
        borderTop: '1px solid #f1f5f9',
        padding: '16px 32px',
        textAlign: 'center',
    },
    footerText: {
        fontSize: 11,
        fontWeight: 600,
        color: '#94a3b8',
        margin: 0,
        letterSpacing: '0.05em',
    },
    loadingWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
    },
    spinner: {
        width: 48,
        height: 48,
        border: '4px solid rgba(255,255,255,0.15)',
        borderTop: '4px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spinLoader 0.8s linear infinite',
    },
    loadingText: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        margin: 0,
    },
};

export default PaymentStatus;
